import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Minus, Tag } from 'lucide-react';
import { Footer, Nav } from './LandingPage';
import {
  PERIODE,
  PERIODE_PER,
  hargaRingkas,
  usePublicPlans,
  useSiteContent,
} from '@/components/marketing/usePublic';
import { formatBytes, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';

/** Baris perbandingan: label di kiri, satu sel per paket. */
/**
 * Frekuensi jadwal paling rapat yang diizinkan sebuah paket.
 *
 * Urutannya dari yang paling rapat, dan yang pertama cocok itu jawabannya.
 * Ditulis di sini alih-alih di server karena ini soal cara menampilkan, bukan
 * soal aturan: penegakannya tetap ada di `quota.service.js`.
 */
const URUT_FREKUENSI = [
  ['hourly', 'Tiap jam'],
  ['daily', 'Harian'],
  ['weekly', 'Mingguan'],
  ['monthly', 'Bulanan'],
];

const FREKUENSI_TERAPAT = (p) => {
  const punya = p.scheduleFrequencies ?? [];
  const cocok = URUT_FREKUENSI.find(([id]) => punya.includes(id));
  return cocok ? cocok[1] : <Centang ya={false} />;
};

const Baris = ({ label, render, plans, tebal = false }) => (
  <tr className="border-b border-white/8">
    <td
      className={cn(
        'py-3.5 pr-4 text-sm',
        tebal ? 'font-semibold text-white' : 'text-white/60',
      )}
    >
      {label}
    </td>
    {plans.map((p) => (
      <td key={p.id} className="px-3 py-3.5 text-center text-sm text-white/80">
        {render(p)}
      </td>
    ))}
  </tr>
);

const Centang = ({ ya }) =>
  ya ? (
    <Check className="mx-auto size-4 text-signal-400" aria-label="Ya" />
  ) : (
    <Minus className="mx-auto size-4 text-white/25" aria-label="Tidak" />
  );

/**
 * Halaman harga lengkap: kartu ringkas plus tabel perbandingan seluruh batas
 * tiap paket. Harganya hidup dari basis data, jadi perubahan admin langsung
 * tampil tanpa deploy.
 */
export const HargaPage = () => {
  const plans = usePublicPlans() ?? [];
  const { konten } = useSiteContent();

  // Kode diskon yang dibawa dari pengumuman, lewat /harga?kode=HMT20.
  const [pencarian] = useSearchParams();
  const kode = (pencarian.get('kode') ?? '').trim().toUpperCase();

  // Sudah masuk atau belum menentukan ke mana tombol paket mengantar.
  const sudahMasuk = useAuthStore((st) => st.status === 'authenticated');

  /**
   * Ke mana tombol sebuah paket mengantar.
   *
   * Yang sudah masuk langsung ke halaman pembayaran dengan paket dan kodenya
   * terbawa. Yang belum dilempar ke halaman masuk dengan tujuan yang sama
   * disematkan, supaya sesudah masuk ia mendarat tepat di tempat ia berhenti,
   * bukan di dashboard yang menuntutnya mengulang seluruh pilihan.
   */
  const tujuan = (planId) => {
    if (planId === 'free') return sudahMasuk ? '/dashboard' : '/daftar';

    const params = new URLSearchParams({ plan: planId });
    if (kode) params.set('kode', kode);
    const billing = `/billing?${params.toString()}`;

    return sudahMasuk ? billing : `/daftar?redirect=${encodeURIComponent(billing)}`;
  };

  return (
    <div className="min-h-screen bg-[var(--color-void-950)] text-white">
      <Nav />

      <main className="mx-auto max-w-6xl px-5 pt-32 pb-20 sm:px-6">
        {kode && (
          <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-signal-400/40 bg-signal-400/10 px-4 py-3">
            <Tag className="size-4 shrink-0 text-signal-400" aria-hidden="true" />
            <p className="text-sm">
              <span className="font-bold text-signal-400">Kode {kode} aktif.</span>{' '}
              <span className="text-white/70">
                Potongannya dihitung otomatis saat Anda memilih paket. Yang belum punya akun
                diminta masuk dulu, lalu langsung diantar ke pembayaran.
              </span>
            </p>
          </div>
        )}

        <p className="text-xs font-bold tracking-[0.14em] text-signal-400 uppercase">Harga</p>
        <h1 className="display mt-4 max-w-2xl text-4xl font-extrabold sm:text-5xl">
          Satu halaman, semua angkanya
        </h1>
        <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-white/50">
          Tidak ada biaya tersembunyi dan tidak ada "hubungi sales". Semua batas tiap paket
          tertulis di tabel ini.
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {plans.map((p) => (
            <div
              key={p.id}
              className={cn(
                'relative flex flex-col rounded-2xl border p-6',
                p.tagline || p.id === 'growth'
                  ? 'border-signal-400/40 bg-signal-400/[0.06]'
                  : 'border-white/8 bg-white/[0.02]',
              )}
            >
              {(p.tagline || p.id === 'growth') && (
                <span className="absolute -top-2.5 left-6 rounded-full bg-signal-400 px-2.5 py-0.5 text-[10px] font-extrabold text-void-950 uppercase">
                  {p.tagline || 'Terpopuler'}
                </span>
              )}
              <p className="text-sm font-bold text-white/70">{p.name}</p>
              <p className="mt-3 text-3xl font-extrabold">
                {p.priceIdr === 0 ? 'Gratis' : `Rp${hargaRingkas(p.priceIdr)}`}
              </p>
              <p className="mt-1 text-xs text-white/40">
                per {PERIODE_PER[p.periodDays] ?? `${p.periodDays} hari`}
              </p>
              <Link
                to={tujuan(p.id)}
                className={cn(
                  'mt-5 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-bold transition-colors',
                  p.tagline || p.id === 'growth'
                    ? 'bg-signal-400 text-void-950 hover:bg-signal-300'
                    : 'border border-white/15 text-white hover:bg-white/5',
                )}
              >
                {p.id === 'free' ? 'Mulai' : kode ? 'Pakai kode ini' : 'Pilih paket'}
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          ))}
        </div>

        {plans.length > 0 && (
          <div className="mt-16 overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b-2 border-white/15">
                  <th className="py-3 pr-4 text-left text-sm font-bold text-white/40">
                    Perbandingan
                  </th>
                  {plans.map((p) => (
                    <th key={p.id} className="px-3 py-3 text-center text-sm font-bold text-white">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <Baris
                  label="Harga"
                  plans={plans}
                  tebal
                  render={(p) => (p.priceIdr === 0 ? 'Gratis' : `Rp${hargaRingkas(p.priceIdr)}`)}
                />
                <Baris
                  label="Masa berlaku"
                  plans={plans}
                  render={(p) => PERIODE[p.periodDays] ?? `${p.periodDays} hari`}
                />
                <Baris
                  label="Baris data per periode"
                  plans={plans}
                  render={(p) => formatNumber(p.maxRecords)}
                />
                <Baris label="Job tersimpan" plans={plans} render={(p) => p.maxJobs} />
                <Baris
                  label="Crawl bersamaan"
                  plans={plans}
                  render={(p) => p.maxConcurrentRuns}
                />
                <Baris
                  label="Halaman per job"
                  plans={plans}
                  render={(p) => formatNumber(p.maxPagesPerJob)}
                />
                <Baris
                  label="Penyimpanan"
                  plans={plans}
                  render={(p) => formatBytes(p.storageBytes)}
                />
                <Baris
                  label="Retensi data"
                  plans={plans}
                  render={(p) => `${p.retentionDays} hari`}
                />
                <Baris
                  label="Penjadwalan otomatis"
                  plans={plans}
                  render={(p) => <Centang ya={p.scheduleAllowed} />}
                />
                {/* Frekuensinya berbeda antar paket, dan itu justru yang
                    paling sering ditanyakan. Tanda centang saja membuat
                    pembeli mengira semua paket bisa jalan tiap jam. */}
                <Baris
                  label="Jadwal paling rapat"
                  plans={plans}
                  render={(p) => FREKUENSI_TERAPAT(p)}
                />
                <Baris
                  label="Jadwal aktif sekaligus"
                  plans={plans}
                  render={(p) => (p.maxSchedules > 0 ? p.maxSchedules : <Centang ya={false} />)}
                />
                <Baris
                  label="Akses API"
                  plans={plans}
                  render={(p) => <Centang ya={p.apiAccess} />}
                />
                <Baris
                  label="Bisa tambah kuota (add-on)"
                  plans={plans}
                  render={(p) => <Centang ya={p.addOnAllowed} />}
                />
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-10 space-y-3 text-sm text-white/40">
          <p>
            Semua paket sudah termasuk deteksi kolom otomatis, mode AI dengan kunci Anda
            sendiri, ekspor CSV, JSON, NDJSON, dan Excel, serta crawling yang sopan pada situs
            sumber: robots.txt diikuti dan ada jeda di antara permintaan.
          </p>
          <p>
            Paket tidak diperpanjang otomatis, jadi tidak ada tagihan yang muncul tanpa Anda
            minta. Kalau Anda memperpanjang sebelum masa berlakunya habis, sisa harinya
            ditambahkan, bukan hangus. Kuota juga bisa ditambah di tengah periode lewat add-on,
            dengan harga yang dihitung menurut sisa harinya.
          </p>
        </div>

        <p className="mt-6 text-sm text-white/40">
          Butuh lebih dari paket Business? Hubungi kami
          {konten?.contactEmail ? (
            <>
              {' '}
              di{' '}
              <a href={`mailto:${konten.contactEmail}`} className="text-signal-400 hover:underline">
                {konten.contactEmail}
              </a>
            </>
          ) : (
            ' lewat halaman Lapor Bug setelah masuk'
          )}
          .
        </p>
      </main>

      <Footer kontak={konten?.contactEmail} konten={konten} />
    </div>
  );
};
